// `avatar sync [--version <tag>] [--dry-run] [--force]` — v3.
//
// Tải team-ai-pack tarball mới từ Supabase (thay git submodule update) + tạo
// symlink farm + merge settings + re-apply tools.
//
// Flow:
//   1. Locate project root (cwd có .claude/pack/)
//   2. getValidIdToken → fetchSignedPackUrl(version?) → download+extract → manifest
//      (--version pin bản cụ thể; không flag → server trả stable mới nhất)
//   3. --dry-run? show preview (version diff) và exit
//   4. syncAllMountDirs() → symlink
//   5. merge settings + re-apply tools

import { readFile } from "node:fs/promises";
import { basename, join, resolve } from "node:path";
import type { Command } from "commander";

import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { ensureGitnexusInstalledAndLatest } from "../lib/ensure-gitnexus-installed-and-latest.js";
import { ensureSecurityScannersInstalled } from "../lib/ensure-security-scanners-installed.js";
import { installPackFromLocalDir } from "../lib/install-pack-from-local-dir.js";
import { mergePackSettingsIntoProjectSettings } from "../lib/merge-pack-settings-into-project-settings.js";
import { writePackManifest } from "../lib/pack-manifest-store.js";
import { buildSyncPreview } from "../lib/preview-team-pack-sync-changes-for-dry-run.js";
import { mergeRootClaudeMdManaged } from "../lib/project-tree-scaffolder.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import { reapplyEnabledToolsAfterSync } from "../lib/reapply-enabled-tools-after-sync.js";
import {
  downloadAndExtractPack,
  fetchSignedPackUrl,
  InvalidIdTokenError,
} from "../lib/supabase-pack-downloader.js";
import {
  type SyncMountedDirResult,
  syncAllMountDirs,
} from "../lib/symlink-farm-for-team-pack-mount-dirs.js";
import { TEAM_PACK_RELATIVE_PATH } from "../lib/team-pack-submodule-manager.js";
import { log } from "../lib/terminal-logger.js";
import { getValidIdToken } from "../lib/user-config-store.js";

interface SyncOptions {
  force?: boolean;
  packVersion?: string;
  dryRun?: boolean;
  local?: string;
}

async function readLocalPackVersion(sourceDir: string): Promise<string> {
  try {
    const raw = await readFile(join(sourceDir, ".pack-version"), "utf8");
    const v = raw.trim();
    if (v) return v;
  } catch {
    // no .pack-version → fall through to a dated local tag
  }
  return `local-${new Date().toISOString().slice(0, 10)}`;
}

function objectToVersion(object: string): string {
  return object.replace(/^pack-/, "").replace(/\.tar\.gz$/, "");
}

// Lấy metadata để render lại managed block. Vì marker bọc TOÀN BỘ CLAUDE.md
// (kể cả # tên + mô tả), sync không parse từ file cũ (sẽ kéo nội dung cũ rác
// quay lại). Thay vào đó:
//   - projectName  = tên thư mục workspace (ổn định, là danh tính thật).
//   - teamOwner    = đọc best-effort từ CLAUDE.md cũ TRONG marker (chỉ field
//                    metadata thuần, không phải nội dung tự do) — giữ giá trị
//                    init đã set; không có thì rỗng.
//   - description  = rỗng (init set; sync không tái tạo mô tả tự do).
async function readClaudeMdMeta(
  projectRoot: string,
  claudeMdPath: string,
): Promise<{ projectName: string; projectDescription: string; teamOwner: string }> {
  const projectName = basename(projectRoot) || "workspace";
  let owner = "";
  try {
    const text = await readFile(claudeMdPath, "utf8");
    owner = text.match(/^-\s*Team owner:\s*(.+)$/m)?.[1]?.trim() ?? "";
  } catch {
    // ignore — owner rỗng
  }
  return { projectName, projectDescription: "", teamOwner: owner };
}

async function syncAction(opts: SyncOptions): Promise<void> {
  // Guard: cwd phải là Avatar workspace (marker .claude/avatar.json). Đứng nhầm
  // workspace avatar-po → báo dùng 'avatar-po'. Thay check pathExists(.claude/) cũ
  // (quá lỏng: mọi thư mục có .claude/ đều qua, kể cả workspace của CLI kia).
  const projectRoot = assertInAvatarWorkspaceOrExit(process.cwd());
  const claudeDir = join(projectRoot, ".claude");
  const packDir = join(projectRoot, TEAM_PACK_RELATIVE_PATH);

  // --dry-run: chỉ preview, không tải.
  if (opts.dryRun && !opts.local) {
    const preview = await buildSyncPreview(packDir, claudeDir, opts.packVersion);
    log.info(`Pack version hiện tại: ${preview.currentVersion}`);
    log.info(`Target version:        ${preview.targetVersion}`);
    log.info("\nMount dir statuses:");
    for (const m of preview.mountDirStatuses) {
      log.plain(`  ${m.dir.padEnd(12)} ${m.status}`);
    }
    log.info("\nDry-run done. Không apply. Bỏ --dry-run để thực thi.");
    return;
  }

  let installedVersion: string;

  // --local: hermetic install from a local pack source. No token, no Supabase,
  // no endpoint allowlist. Reuses the same symlink farm + settings merge below.
  if (opts.local) {
    if (opts.packVersion) {
      log.error("--local không dùng chung với --pack-version (version chỉ áp dụng cho Supabase).");
      process.exit(1);
    }
    const sourceDir = resolve(opts.local);
    if (opts.dryRun) {
      log.info(`[dry-run] Sẽ cài pack từ local: ${sourceDir} → ${packDir}`);
      log.info("[dry-run] Sau đó dựng lại symlink farm + merge settings (không cần mạng).");
      return;
    }
    log.info(`Cài pack từ local source: ${sourceDir}`);
    try {
      await installPackFromLocalDir(sourceDir, packDir);
      installedVersion = await readLocalPackVersion(sourceDir);
      await writePackManifest(packDir, {
        version: installedVersion,
        downloadedObject: `local:${sourceDir}`,
        extractedAt: new Date().toISOString(),
      });
      log.success(`Đã cài pack local ${installedVersion}`);
    } catch (err) {
      log.error(`Cài pack local thất bại: ${err instanceof Error ? err.message : err}`);
      process.exit(1);
    }
  } else {
    // Tải tarball mới.
    let idToken: string;
    try {
      idToken = await getValidIdToken();
    } catch (err) {
      log.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }

    log.info(
      opts.packVersion
        ? `Tải team-ai-pack ${opts.packVersion} từ Supabase...`
        : "Tải team-ai-pack (stable mới nhất) từ Supabase...",
    );
    try {
      const info = await fetchSignedPackUrl(idToken, opts.packVersion);
      await downloadAndExtractPack(info.url, packDir);
      installedVersion = opts.packVersion ?? objectToVersion(info.object);
      await writePackManifest(packDir, {
        version: installedVersion,
        downloadedObject: info.object,
        extractedAt: new Date().toISOString(),
      });
      log.success(`Đã cài pack ${installedVersion}`);
    } catch (err) {
      if (err instanceof InvalidIdTokenError) {
        log.error(`${err.message}`);
      } else {
        log.error(`Tải pack thất bại: ${err instanceof Error ? err.message : err}`);
      }
      process.exit(1);
    }
  }

  log.info("Tạo symlink farm...");
  const results = await syncAllMountDirs(packDir, claudeDir, opts.force === true);
  reportResults(results, opts.force === true);

  // Merge settings + re-apply tools (giữ nguyên logic v2).
  log.info("Merge pack settings.json template vào project settings.json...");
  try {
    const mergeResult = await mergePackSettingsIntoProjectSettings(projectRoot);
    switch (mergeResult.action) {
      case "merged":
        log.success(`  ✓ settings.json merged (${mergeResult.changes.join("; ")})`);
        break;
      case "no-change":
        log.info("  - settings.json đã sync.");
        break;
      case "no-pack-template":
        log.dim("  - Pack không có templates/settings.json.tpl, skip.");
        break;
    }
  } catch (err) {
    log.warn(`  ! Merge settings.json fail: ${err instanceof Error ? err.message : err}.`);
  }

  try {
    await reapplyEnabledToolsAfterSync(projectRoot);
  } catch (err) {
    log.warn(`  ! Re-apply tools fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Cập nhật managed block trong CLAUDE.md (Workflows&Rules, hướng dẫn pack...)
  // mà giữ context user. Fail-open: lỗi → cảnh báo, không chặn sync.
  log.info("Cập nhật managed block trong CLAUDE.md...");
  try {
    const meta = await readClaudeMdMeta(projectRoot, join(projectRoot, "CLAUDE.md"));
    const vars = {
      projectName: meta.projectName,
      projectDescription: meta.projectDescription,
      teamOwner: meta.teamOwner,
      avatarVersion: readCliVersion(),
      packVersion: installedVersion,
      lastScan: new Date().toISOString(),
      gitnexusSection: "",
    };
    const r = await mergeRootClaudeMdManaged(projectRoot, vars);
    switch (r.outcome) {
      case "replaced-block":
        log.success("  ✓ CLAUDE.md managed block đã cập nhật (giữ context user ngoài block).");
        break;
      case "replaced-whole":
        log.success("  ✓ CLAUDE.md (chưa có marker) → thay hoàn toàn bằng bản Avatar mới.");
        break;
      case "skipped-no-file":
        log.dim("  - Chưa có CLAUDE.md (chạy 'avatar init' để tạo), skip.");
        break;
    }
  } catch (err) {
    log.warn(`  ! Cập nhật CLAUDE.md fail: ${err instanceof Error ? err.message : err}.`);
  }

  // GitNexus = hạ tầng cốt lõi → mỗi sync đảm bảo đã cài + bản mới nhất.
  // Fail-soft: lỗi npm/mạng → warn, không chặn sync.
  try {
    await ensureGitnexusInstalledAndLatest();
  } catch (err) {
    log.warn(`  ! GitNexus check fail: ${err instanceof Error ? err.message : err}.`);
  }

  // Security scanner (gitleaks secret + trivy CVE) cho pull-request-checker.
  // Cài cái thiếu qua brew, fail-soft (không brew / lỗi → gate tự skip).
  try {
    ensureSecurityScannersInstalled();
  } catch (err) {
    log.warn(`  ! Scanner check fail: ${err instanceof Error ? err.message : err}.`);
  }

  log.success(`Synced team-ai-pack → ${installedVersion}.`);
}

function reportResults(results: SyncMountedDirResult[], force: boolean): void {
  for (const r of results) {
    switch (r.action) {
      case "created":
        log.info(`  ✓ ${r.dir} → symlinked (new)`);
        break;
      case "updated":
        log.info(`  ✓ ${r.dir} → symlink refreshed`);
        break;
      case "backed-up-and-linked":
        log.info(`  ✓ ${r.dir} → symlinked (backup: ${r.backupPath})`);
        break;
      case "source-missing":
        log.warn(`  - ${r.dir} → pack không có dir này, skip`);
        break;
      case "skipped-conflict":
        log.warn(`  ! ${r.dir} → CONFLICT: existing real dir. Dùng --force để backup + override.`);
        break;
    }
  }
  const conflicts = results.filter((r) => r.action === "skipped-conflict").length;
  if (conflicts > 0 && !force) {
    log.warn(`${conflicts} mount dir(s) skip do conflict. Chạy lại với --force.`);
  }
}

export function registerSyncCommand(program: Command): void {
  program
    .command("sync")
    .description("Tải team-ai-pack mới nhất (tarball Supabase) + tạo symlink farm")
    .option("--force", "Override .claude/<dir>/ nếu là real dir (backup trước)")
    .option("--pack-version <tag>", "Pin version cụ thể (vd: v0.2.0 hoặc v0.2.0-beta.0)")
    .option("--dry-run", "Hiển thị preview, không apply")
    .option("--local <path>", "Cài pack từ thư mục local thay vì tải Supabase (sandbox/dev)")
    .action(syncAction);
}
